import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { partsApi } from '@/api/parts.api'
import { Part } from '@/types/parts'

interface PartsState {
  parts: Part[]
  isLoading: boolean
  isTableLoading: boolean
  error: string | null
}

const initialState: PartsState = {
  parts: [],
  isLoading: false,
  isTableLoading: false,
  error: null,
}

export const getAllPartsData = createAsyncThunk<Part[], void>('parts/getAllParts', (_, { rejectWithValue }) =>
  partsApi
    .getAllParts()
    .then((res) => {
      return res.data
    })
    .catch((err) => {
      return rejectWithValue(err.message)
    }),
)

export const createPartData = createAsyncThunk<Part, any>('parts/createPart', (partData, { rejectWithValue }) =>
  partsApi
    .createPart(partData)
    .then((res) => {
      return res
    })
    .catch((err) => {
      return rejectWithValue(err.message)
    }),
)

export const updatePartData = createAsyncThunk<Part, { id: string; data: any }>(
  'parts/updatePart',
  ({ id, data }, { rejectWithValue }) =>
    partsApi
      .updatePart(id, data)
      .then((res) => {
        return res
      })
      .catch((err) => {
        return rejectWithValue(err.message)
      }),
)

export const deletePartData = createAsyncThunk<string, string>('parts/deletePart', (id, { rejectWithValue }) =>
  partsApi
    .deletePart(id)
    .then(() => {
      return id
    })
    .catch((err) => {
      return rejectWithValue(err.message)
    }),
)

export const partsSlice = createSlice({
  name: 'parts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllPartsData.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getAllPartsData.fulfilled, (state, action) => {
        state.isLoading = false
        state.parts = action.payload
      })
      .addCase(getAllPartsData.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      .addCase(createPartData.pending, (state) => {
        state.isTableLoading = true
        state.error = null
      })
      .addCase(createPartData.fulfilled, (state, action) => {
        state.isTableLoading = false
        state.parts.push(action.payload)
      })
      .addCase(createPartData.rejected, (state, action) => {
        state.isTableLoading = false
        state.error = action.payload as string
      })
      .addCase(updatePartData.pending, (state) => {
        state.isTableLoading = true
        state.error = null
      })
      .addCase(updatePartData.fulfilled, (state, action) => {
        state.isTableLoading = false
        const index = state.parts.findIndex((part) => part.id === action.payload.id)
        if (index !== -1) {
          state.parts[index] = action.payload
        }
      })
      .addCase(updatePartData.rejected, (state, action) => {
        state.isTableLoading = false
        state.error = action.payload as string
      })
      .addCase(deletePartData.pending, (state) => {
        state.isTableLoading = true
        state.error = null
      })
      .addCase(deletePartData.fulfilled, (state, action) => {
        state.isTableLoading = false
        state.parts = state.parts.filter((part) => part.id !== action.payload)
      })
      .addCase(deletePartData.rejected, (state, action) => {
        state.isTableLoading = false
        state.error = action.payload as string
      })
  },
})

export const { clearError } = partsSlice.actions
export default partsSlice.reducer
